import { ChangeEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import AddLink from '../ui/AddLink';
import { ErrorNotice } from '../ui/States';
import { fmt } from '../../lib/format';
import { errorMessage } from '../../lib/programs';
import { EventDetail, dayLabel } from '../../lib/events';
import { UploadFile, readUpload } from './externalFiles';
import { AGENDA_IMPORT_SLOT } from './AgendaPanel';
import { field, useAgenda } from './agendaTypes';

interface AgendaImport {
  dryRun: boolean;
  sheet: string;
  format: string;
  rows: number;
  skipped: number;
  warnings: string[];
  sessions: number;
  sessionsExisting: number;
  sessionsCreated: number;
  speakers: number;
  matchedMembers: number;
  matchedContacts: number;
  newContacts: number;
  ambiguousNames: number;
  speakersExisting: number;
  speakersCreated: number;
  parts: number;
  partsCreated: number;
  missingBio: number;
  missingPhoto: number;
}

/**
 * The planning-sheet import: a section under the tab's opening sentence while the agenda is empty; once sessions
 * exist it folds away behind "Planning sheet import" in the tab's add bar.
 */
export default function AgendaImportPanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<UploadFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [day, setDay] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => setSlot(document.getElementById(AGENDA_IMPORT_SLOT)), []);
  const agenda = useAgenda(eventId);
  const event = useQuery<EventDetail>(['event', eventId], async () => (await api.get(`/events/${eventId}`)).data.data);
  const send = async (confirm: boolean) =>
    (
      await api.post(`/events/${eventId}/agenda/import`, {
        fileName: file?.name,
        content: file?.content,
        day: day || undefined,
        confirm,
      })
    ).data.data as AgendaImport;
  const preview = useMutation(() => send(false));
  const save = useMutation(() => send(true), {
    onSuccess: () => {
      setFile(null);
      queryClient.invalidateQueries('event-agenda');
      queryClient.invalidateQueries('event');
    },
  });
  if (!canEdit || !agenda.data) return null;
  const empty = !agenda.data.sessions.length;
  if (!empty && !revealed && !save.data && !file)
    return <AddLink label="Planning sheet import" onClick={() => setRevealed(true)} />;
  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    setFile(null);
    setFileError(null);
    preview.reset();
    save.reset();
    if (!f) return;
    try {
      const up = await readUpload(f);
      if (!/\.xlsx$/i.test(up.name)) throw new Error('Upload the planning sheet as an .xlsx file.');
      setFile(up);
    } catch (err) {
      setFileError((err as Error).message);
    }
  };
  const r = save.data ?? preview.data;
  const days = event.data?.event.days ?? [];
  const section = (
    <Panel className="mt-14 max-md:mt-9">
      <PanelHeader title="Import the planning sheet" note={file ? file.name : undefined} />
      <PanelBody className="text-[15px] leading-[22px]">
        <p className="text-ink-2 max-w-[72ch] mb-4">
          Upload the master tracker (its &ldquo;Speakers &amp; Sessions&rdquo; tab) or the planning sheet
          (&ldquo;Events, Speakers&rdquo;). Check it first: nothing is added until you confirm, and sessions and
          speakers already on the agenda are not doubled.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="inline-flex items-center justify-center min-h-[44px] px-4 py-[11px] rounded-sm border border-line-strong text-ink font-semibold text-[15px] leading-5 cursor-pointer hover:bg-selected focus-within:outline focus-within:outline-2 focus-within:outline-ink focus-within:outline-offset-2">
            {file ? 'Choose another file' : 'Choose the .xlsx file'}
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={pick}
              className="sr-only"
            />
          </label>
          {file && days.length > 1 ? (
            <label className="block flex-[0_1_380px]">
              <span className={field.label}>Day of these sessions, if the sheet has no dates</span>
              <select
                className={field.input}
                value={day}
                onChange={(e) => {
                  setDay(e.target.value);
                  preview.reset();
                }}
              >
                <option value="">The sheet decides (first day when it has no dates)</option>
                {days.map((d) => (
                  <option key={d.date} value={d.date}>
                    {dayLabel(d.date)}
                    {d.label ? ` · ${d.label}` : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {file ? (
            <>
              <Button disabled={preview.isLoading} onClick={() => preview.mutate()}>
                Check the file
              </Button>
              <Button
                variant={empty && !agenda.data.speakers.length ? 'primary' : 'secondary'}
                disabled={!preview.data || save.isLoading || Boolean(save.data)}
                onClick={() => save.mutate()}
              >
                Add to the agenda
              </Button>
            </>
          ) : null}
        </div>
        {file && !preview.data ? (
          <p className="text-small text-ink-3 mt-2">
            &ldquo;Add to the agenda&rdquo; turns on after you check the file.
          </p>
        ) : null}
        {fileError ? (
          <div className="mt-3">
            <ErrorNotice>{fileError}</ErrorNotice>
          </div>
        ) : null}
        {preview.error || save.error ? (
          <div className="mt-3">
            <ErrorNotice>{errorMessage(preview.error || save.error, 'The file could not be read.')}</ErrorNotice>
          </div>
        ) : null}
        {r ? (
          <div className="mt-4 border-t border-line pt-3 text-ink space-y-1 max-w-[72ch]">
            <p>
              Sheet &ldquo;{r.sheet}&rdquo;: {fmt(r.rows)} rows read{r.skipped ? `, ${fmt(r.skipped)} skipped` : ''}.
            </p>
            <p>
              {fmt(r.sessions)} sessions, {fmt(r.sessionsExisting)} already on the agenda.
            </p>
            <p>
              {fmt(r.speakers)} speakers: {fmt(r.matchedMembers)} members, {fmt(r.matchedContacts)} known contacts,{' '}
              {fmt(r.newContacts)} new; {fmt(r.speakersExisting)} already speaking. {fmt(r.parts)} speaking parts.
            </p>
            {r.ambiguousNames ? (
              <p>
                {fmt(r.ambiguousNames)} name(s) match more than one person: check them on the speaker list after adding.
              </p>
            ) : null}
            <p>
              Missing a bio: {fmt(r.missingBio)}. Missing a photo: {fmt(r.missingPhoto)}.
            </p>
            {r.warnings.map((w) => (
              <p key={w} className="text-ink-2">
                {w}
              </p>
            ))}
            {!r.dryRun ? (
              <p className="font-semibold">
                Added {fmt(r.sessionsCreated)} sessions, {fmt(r.speakersCreated)} speakers and {fmt(r.partsCreated)}{' '}
                speaking parts.
              </p>
            ) : null}
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
  return slot ? createPortal(section, slot) : section;
}
